import { Box, Typography, makeStyles } from '@dxb-web-ui-framework/design-system';
import clsx from 'clsx';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import React, { useCallback, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { useBulkSelectedAuthorizations } from '../contexts';
import { ProcessStatuses } from './ApproveRejectAuthFlowPresenter';

export interface ActionAuthorizationButtonProps {
  transactionNo: string;
  processStatus?: string;
  callback?: Function;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  authActionText: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
    color: 'white',
    '&:hover': {
      cursor: 'pointer',
      opacity: '0.8',
    },
  },
  reject: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(2.8)}px`,
    backgroundColor: theme.palette.primary.main,
  },
  approve: {
    backgroundColor: theme.palette.success.main,
  },
  warningText: {
    backgroundColor: theme.palette.warning.main,
    '&:hover': {
      cursor: 'default',
      opacity: '1',
    },
  },
  span: {
    margin: 'auto',
  },
  textDisabled: {
    backgroundColor: `${theme.palette.text.disabled} !important`,
    '&:hover': {
      cursor: 'default',
      opacity: '1',
    },
  },
}));

const ActionAuthorizationButtons: FC<ActionAuthorizationButtonProps> = ({
  transactionNo,
  callback,
  processStatus,
  children,
}) => {
  const bulkSelectedAuthorizations = useBulkSelectedAuthorizations();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();

  const { t } = useTranslation();

  const onClick = useCallback(
    (e, name) => {
      if (callback) {
        callback(e, name, transactionNo, false);
      }
    },
    [transactionNo, callback],
  );

  const isAuthorizationSelectedForBulk =
    bulkSelectedAuthorizations &&
    !isEmpty(bulkSelectedAuthorizations) &&
    Boolean(bulkSelectedAuthorizations.find(bs => bs.transactionId === transactionNo));

  const classes = useStyles();

  if (processStatus && processStatus === ProcessStatuses.PENDING) {
    return (
      <Box className={classes.root}>
        {children && <Box>{children}</Box>}
        <Box>
          <Typography
            paragraph
            component='span'
            color='primary'
            variant='body2'
            className={clsx(classes.authActionText, classes.warningText, classes.span)}>
            {t('authorizationListPage.ActionAuthorizationButtons.Pending')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return isMobile ? (
    <Box className={classes.root}>
      {children && (
        <Box display='flex' alignItems='center' flex={1}>
          {children}
        </Box>
      )}
      <Typography
        onClick={(e: any) => !isAuthorizationSelectedForBulk && onClick(e, 'reject')}
        component='span'
        variant='body2'
        className={clsx(classes.authActionText, classes.reject, {
          [classes.textDisabled]: isAuthorizationSelectedForBulk,
        })}>
        {t('authorizationListPage.ActionAuthorizationButtons.Reject')}
      </Typography>
      <Typography
        onClick={(e: any) => !isAuthorizationSelectedForBulk && onClick(e, 'approve')}
        component='span'
        variant='body2'
        className={clsx(classes.authActionText, classes.approve, {
          [classes.textDisabled]: isAuthorizationSelectedForBulk,
        })}>
        {t('authorizationListPage.ActionAuthorizationButtons.Approve')}
      </Typography>
    </Box>
  ) : (
    <Box className={classes.root}>
      {children && <Box>{children}</Box>}
      <Box>
        <Typography
          onClick={(e: any) => !isAuthorizationSelectedForBulk && onClick(e, 'reject')}
          paragraph
          component='span'
          variant='body2'
          className={clsx(classes.authActionText, classes.reject, {
            [classes.textDisabled]: isAuthorizationSelectedForBulk,
          })}>
          {t('authorizationListPage.ActionAuthorizationButtons.Reject')}
        </Typography>
      </Box>
      <Box>
        <Typography
          onClick={(e: any) => !isAuthorizationSelectedForBulk && onClick(e, 'approve')}
          paragraph
          component='span'
          variant='body2'
          className={clsx(classes.authActionText, classes.approve, {
            [classes.textDisabled]: isAuthorizationSelectedForBulk,
          })}>
          {t('authorizationListPage.ActionAuthorizationButtons.Approve')}
        </Typography>
      </Box>
    </Box>
  );
};

export { ActionAuthorizationButtons };
